/**
 * Fees Component — Case Associates LP Override
 *
 * Overrides the base landing-page Fees to use bg-secondary card backgrounds
 * and bg-primary pet-friendly badge.
 */

import {
  useProperty,
  useContentToken,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import type { ComponentContentSchema, ComponentDesignSchema } from '@vibesites/runtime';
import { defineComponentRequirements, details } from '@vibesites/tokens';

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined' && s !== '-';
}

function hasNumericValue(val: unknown): boolean {
  if (!hasValue(val)) return false;
  const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
  return !isNaN(num) && num > 0;
}

function formatCurrency(val: unknown): string {
  if (!hasValue(val)) return '';
  const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
  if (isNaN(num) || num <= 0) return '';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function Fees() {
  const property = useProperty();
  const missingPetPricingMessage = 'Contact leasing for more details.';

  const sectionTitle = useContentToken('fees.title');
  const feesTitle = useContentToken('fees.fees_title');
  const petTitle = useContentToken('fees.pet_title');
  const petBadgeText = useContentToken('fees.pet_badge_text');

  const isPetFriendly = property.property_ispetfriendly;

  const summaryRows: { label: string; value: string }[] = [];

  const appFee = formatCurrency(property.property_application_fee);
  const adminFee = formatCurrency(property.property_admin_fee);
  if (appFee) summaryRows.push({ label: 'Application Fee', value: appFee });
  if (adminFee) summaryRows.push({ label: 'Admin Fee', value: adminFee });

  const allowsCats = property.property_allows_cats;
  const allowsDogs = property.property_allows_dogs;
  const allowedTypes: string[] = [];
  if (allowsCats) allowedTypes.push('Cats');
  if (allowsDogs) allowedTypes.push('Dogs');
  if (allowedTypes.length > 0) {
    summaryRows.push({ label: 'Pets Allowed', value: allowedTypes.join(' & ') });
  }

  const maxPets = property.property_max_pets as string | null;
  if (hasValue(maxPets) && maxPets !== '0') {
    summaryRows.push({ label: 'Max Pets', value: String(maxPets) });
  }

  const maxWeight = property.property_max_pet_weight_limit as string | null;
  if (hasValue(maxWeight)) {
    const w = parseFloat(String(maxWeight).replace(/[^0-9.]/g, ''));
    summaryRows.push({
      label: 'Weight Limit',
      value: isNaN(w) || w <= 0 ? 'No Limit' : `${w} lbs`,
    });
  }

  const petFee = formatCurrency(property.property_upfront_pet_fee);
  const petDeposit = formatCurrency(property.property_pet_deposit);
  const monthlyRent = formatCurrency(property.property_monthly_pet_rent);
  if (petFee) summaryRows.push({ label: 'One-Time Pet Fee', value: petFee });
  if (petDeposit) summaryRows.push({ label: 'Pet Deposit', value: petDeposit });
  if (monthlyRent) summaryRows.push({ label: 'Monthly Pet Rent', value: `${monthlyRent}/pet` });
  const showMissingPetPricingMessage = Boolean(isPetFriendly) && (!petFee || !monthlyRent);

  const restrictions = property.property_pet_restrictions as string | null;
  const hasRestrictions = hasValue(restrictions);

  return (
    <section className="bg-background py-16 md:py-24 font-body">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <EditableToken path="fees.title" type="text" label="Section Title" as="div" style={{ marginBottom: '3rem' }}>
          <h2 className="text-[clamp(1.75rem,3vw,2.5rem)] font-bold font-heading text-foreground uppercase m-0">
            {sectionTitle}
          </h2>
        </EditableToken>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {/* Left: Structured summary */}
          <div className="bg-secondary rounded-xl p-6 md:p-8 flex flex-col gap-5">
            <div className="flex items-center justify-between gap-3">
              <EditableToken path="fees.fees_title" type="text" label="Fees Title" as="div">
                <h3 className="text-xl font-semibold font-heading text-secondary-foreground m-0">{feesTitle}</h3>
              </EditableToken>

              {isPetFriendly && (
                <EditableToken path="fees.pet_badge_text" type="text" label="Pet Badge Text" as="div">
                  <div className="inline-flex items-center gap-1.5 bg-primary py-1.5 px-3 rounded-full text-primary-foreground text-xs font-medium shrink-0">
                    <span>🐾</span>
                    <span>{petBadgeText}</span>
                  </div>
                </EditableToken>
              )}
            </div>

            {summaryRows.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {summaryRows.map((row, i) => (
                  <div key={i} className="flex flex-col">
                    <span className="text-[11px] uppercase tracking-wide text-secondary-foreground font-body mb-0.5 opacity-70">
                      {row.label}
                    </span>
                    <span className="text-sm font-medium text-secondary-foreground font-body">
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <RichTextContent
                  path="fees.fees_content"
                  label="Fees Content"
                  as="div"
                  className="text-base leading-relaxed text-secondary-foreground m-0"
                />
                {showMissingPetPricingMessage && (
                  <p className="text-sm text-secondary-foreground m-0 italic">
                    {missingPetPricingMessage}
                  </p>
                )}
              </>
            )}

            {summaryRows.length > 0 && showMissingPetPricingMessage && (
              <p className="text-sm text-secondary-foreground m-0 italic">
                {missingPetPricingMessage}
              </p>
            )}

            {hasRestrictions && (
              <p className="text-sm text-secondary-foreground m-0 italic border-t border-secondary-foreground/20 pt-4">
                {restrictions}
              </p>
            )}

            {/* Hidden richtext wrapper for when summary grid is shown — satisfies linter */}
            {summaryRows.length > 0 && (
              <div className="hidden">
                <RichTextContent
                  path="fees.fees_content"
                  label="Fees Content"
                  as="div"
                />
              </div>
            )}
          </div>

          {/* Right: Pet policy description */}
          <div className="bg-secondary rounded-xl p-6 md:p-8 flex flex-col gap-4">
            <EditableToken path="fees.pet_title" type="text" label="Pet Title" as="div">
              <h3 className="text-xl font-semibold font-heading text-secondary-foreground m-0">{petTitle}</h3>
            </EditableToken>

            <RichTextContent
              path="fees.pet_policy"
              label="Pet Policy"
              as="div"
              className="text-base leading-relaxed text-secondary-foreground m-0"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Fees',
  displayName: 'Fees & Pet Policy',
  tokens: {
    'fees.title': {
      label: 'Section Title',
      type: 'text',
      description: 'Main heading for the fees and pet policy section',
      defaultValue: 'OUR FEES & PET POLICY',
    },
    'fees.fees_title': {
      label: 'Fees Column Title',
      type: 'text',
      description: 'Heading for the fees column',
      defaultValue: 'One-Time Expenses',
    },
    'fees.fees_content': {
      label: 'Fees Content',
      type: 'richtext',
      description: 'Detailed fees and deposit information',
      defaultField: 'property_fees_deposit_info',
      suggestedFields: ['property_fees_deposit_info'],
      defaultValue: 'Contact us for current fees and deposit information.',
    },
    'fees.pet_title': {
      label: 'Pet Policy Column Title',
      type: 'text',
      description: 'Heading for the pet policy column',
      defaultValue: 'Pet Policy',
    },
    'fees.pet_policy': {
      label: 'Pet Policy Content',
      type: 'richtext',
      description: 'Pet policy details and restrictions',
      defaultField: 'property_pet_policy_description',
      suggestedFields: ['property_pet_policy_description', 'property_pets_blurb'],
      defaultValue: 'Pets welcome! Contact us for details on breed restrictions and deposits.',
    },
    'fees.pet_badge_text': {
      label: 'Pet Badge Text',
      type: 'text',
      description: 'Text shown in the pet-friendly badge',
      defaultValue: 'Pet Friendly',
    },
  },
};

export const designSchema: ComponentDesignSchema = {
  slot: 'Fees',
  displayName: 'Fees & Pet Policy',
  tokens: {
    'color.primary': { label: 'Background Color', type: 'color', description: 'Section background color', cssVariable: '--vibe-color-primary' },
    'color.textOnPrimary': { label: 'Text Color', type: 'color', description: 'Text color on the primary background', cssVariable: '--vibe-color-text-on-primary' },
    'font.heading': { label: 'Heading Font', type: 'font', description: 'Font family for section title', cssVariable: '--vibe-font-heading' },
    'font.body': { label: 'Body Font', type: 'font', description: 'Font family for content text', cssVariable: '--vibe-font-body' },
    'radius.full': { label: 'Badge Radius', type: 'radius', description: 'Border radius for the pet-friendly badge', cssVariable: '--vibe-radius-full' },
  },
};

export const requirements = defineComponentRequirements('Fees', {
  designTokens: ['color.primary', 'color.textOnPrimary', 'font.heading', 'font.body', 'radius.full'],
  dataTokens: [details.optional('property_ispetfriendly', 'Pet friendly flag')],
});

(Fees as any).contentSchema = contentSchema;
(Fees as any).designSchema = designSchema;
(Fees as any).requirements = requirements;

export default Fees;
